// frontend/src/pages/SuperAdmin.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BuildingOfficeIcon, 
  UsersIcon, 
  ChartBarIcon, 
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EnvelopeIcon,
  CreditCardIcon,
  NoSymbolIcon,
  CheckBadgeIcon
} from '@heroicons/react/24/outline';
import { superAdminService } from '../services/api';
import DataTable from '../components/Common/DataTable';
import Modal from '../components/Common/Modal';
import TenantForm from '../components/SuperAdmin/TenantForm';
import { toast } from 'react-hot-toast';

export default function SuperAdmin() {
  const [activeTab, setActiveTab] = useState('tenants');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSuspendConfirm, setShowSuspendConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  
  const queryClient = useQueryClient();

  // Fetch all tenants
  const { data: tenantsData, isLoading: tenantsLoading } = useQuery({
    queryKey: ['superadmin', 'tenants'],
    queryFn: () => superAdminService.getTenants(),
    enabled: activeTab === 'tenants'
  });

  // Fetch global analytics
  const { data: analytics, isLoading: analyticsLoading } = useQuery({
    queryKey: ['superadmin', 'analytics'],
    queryFn: () => superAdminService.getGlobalAnalytics(),
    enabled: activeTab === 'analytics',
    refetchInterval: 30000
  });

  // Mutations
  const suspendTenantMutation = useMutation({
    mutationFn: ({ tenantId, suspend }) => superAdminService.suspendTenant(tenantId, suspend),
    onSuccess: () => {
      toast.success('Tenant status updated successfully');
      queryClient.invalidateQueries(['superadmin', 'tenants']);
      queryClient.invalidateQueries(['superadmin', 'analytics']);
      setShowSuspendConfirm(false);
      setShowDetailsModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update tenant status');
    }
  });

  const deleteTenantMutation = useMutation({
    mutationFn: (tenantId) => superAdminService.deleteTenant(tenantId),
    onSuccess: () => {
      toast.success('Tenant deactivated successfully');
      queryClient.invalidateQueries(['superadmin', 'tenants']);
      queryClient.invalidateQueries(['superadmin', 'analytics']);
      setShowDeleteConfirm(false);
      setShowDetailsModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete tenant');
    }
  });

  const updateSubscriptionMutation = useMutation({
    mutationFn: ({ tenantId, plan, expiryDate }) => 
      superAdminService.updateSubscription(tenantId, { plan, expiryDate }),
    onSuccess: () => {
      toast.success('Subscription updated successfully');
      queryClient.invalidateQueries(['superadmin', 'tenants']);
      setShowDetailsModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update subscription');
    }
  });

  const addSmsCreditMutation = useMutation({
    mutationFn: ({ tenantId, amount }) => 
      superAdminService.addSmsCredit(tenantId, amount),
    onSuccess: (data) => {
      toast.success(`Added KES ${data.added} SMS credit. New balance: KES ${data.newBalance}`);
      queryClient.invalidateQueries(['superadmin', 'tenants']);
      setShowDetailsModal(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to add SMS credit');
    }
  });

  const columns = [
    { key: 'business_name', header: 'Business Name', sortable: true },
	{ key: 'tenant_code', header: 'Tenant Code', sortable: true }, 
    { key: 'email', header: 'Email', sortable: true },
    { key: 'phone', header: 'Phone', sortable: true },
    { key: 'tin_number', header: 'TIN/PIN', sortable: true },
    { 
      key: 'subscription_plan', 
      header: 'Plan', 
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value === 'premium' ? 'bg-purple-100 text-purple-800' :
          value === 'enterprise' ? 'bg-amber-100 text-amber-800' :
          'bg-blue-100 text-blue-800'
        }`}>
          {value.toUpperCase()}
        </span>
      )
    },
    { 
      key: 'subscription_expiry', 
      header: 'Expires', 
      sortable: true, 
      format: 'date',
      render: (value) => {
        const isExpired = new Date(value) < new Date();
        const daysLeft = Math.ceil((new Date(value) - new Date()) / (1000 * 60 * 60 * 24));
        return (
          <div>
            <span className={isExpired ? 'text-red-600 font-semibold' : 'text-green-600'}>
              {new Date(value).toLocaleDateString()}
            </span>
            {!isExpired && daysLeft <= 30 && (
              <p className="text-xs text-orange-500">{daysLeft} days left</p>
            )}
            {isExpired && <p className="text-xs text-red-500">Expired</p>}
          </div>
        );
      }
    },
    { 
      key: 'is_active', 
      header: 'Status', 
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs rounded-full ${
          value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {value ? 'Active' : 'Suspended'}
        </span>
      )
    },
    { key: 'sms_balance', header: 'SMS Balance', format: 'currency' },
    { key: 'created_at', header: 'Registered', format: 'date' }
  ];

  const handleSuspendTenant = (tenant) => {
    setSelectedTenant(tenant);
    setShowSuspendConfirm(true);
  };

  const handleDeleteTenant = (tenant) => {
    setSelectedTenant(tenant);
    setShowDeleteConfirm(true);
  };

  const handleUpdatePlan = (tenant) => {
    const newPlan = prompt('Enter new plan (basic, premium, enterprise):', tenant.subscription_plan);
    if (newPlan && ['basic', 'premium', 'enterprise'].includes(newPlan)) {
      const newExpiry = prompt('Enter new expiry date (YYYY-MM-DD):', tenant.subscription_expiry?.split('T')[0] || '');
      if (newExpiry) {
        updateSubscriptionMutation.mutate({ 
          tenantId: tenant.id, 
          plan: newPlan, 
          expiryDate: newExpiry 
        });
      }
    }
  };

  const handleExtendByDays = (tenant) => {
    const daysToAdd = prompt('Enter number of days to extend subscription:', '30');
    if (daysToAdd && !isNaN(daysToAdd) && parseInt(daysToAdd) > 0) {
      const currentDate = tenant.subscription_expiry ? new Date(tenant.subscription_expiry) : new Date();
      currentDate.setDate(currentDate.getDate() + parseInt(daysToAdd));
      const newExpiry = currentDate.toISOString().split('T')[0];
      updateSubscriptionMutation.mutate({ 
        tenantId: tenant.id, 
        plan: tenant.subscription_plan, 
        expiryDate: newExpiry 
      });
    } else if (daysToAdd) {
      toast.error('Please enter a valid number of days');
    }
  };

  const handleAddSmsCredit = (tenant) => {
    const amount = prompt('Enter SMS top-up amount (KES):', '100');
    if (amount && !isNaN(amount) && parseInt(amount) > 0) {
      addSmsCreditMutation.mutate({ 
        tenantId: tenant.id, 
        amount: parseInt(amount) 
      });
    } else if (amount) {
      toast.error('Please enter a valid amount');
    }
  };

  const actions = [
    {
      label: 'View Details',
      onClick: (row) => {
        setSelectedTenant(row);
        setShowDetailsModal(true);
      },
      icon: 'eye'
    },
    {
      label: 'Update Plan',
      onClick: handleUpdatePlan,
      icon: 'pencil'
    },
    {
      label: 'Suspend/Activate',
      onClick: handleSuspendTenant,
      icon: (row) => row.is_active ? 'x-circle' : 'check-circle'
    },
    {
      label: 'Delete',
      onClick: handleDeleteTenant,
      icon: 'trash',
      className: 'text-red-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
          <p className="text-gray-600">Manage all tenants, subscriptions, and system analytics</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('tenants')}
              className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tenants'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <BuildingOfficeIcon className="h-5 w-5 inline mr-2" />
              Tenants Management
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="h-5 w-5 inline mr-2" />
              Global Analytics
            </button>
            <button
              onClick={() => setActiveTab('revenue')}
              className={`pb-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'revenue'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CurrencyDollarIcon className="h-5 w-5 inline mr-2" />
              Revenue Reports
            </button>
          </nav>
        </div>

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">All Tenants</h2>
                <p className="text-sm text-gray-500">
                  Total: {tenantsData?.total || 0} tenants
                </p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add New Tenant
              </button>
            </div>

            <DataTable
              columns={columns}
              data={tenantsData?.data || []}
              loading={tenantsLoading}
              actions={actions}
              pagination={tenantsData?.pagination}
            />
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Tenants</p>
                    <p className="text-2xl font-bold">{analytics?.data?.totalTenants || 0}</p>
                  </div>
                  <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active Tenants</p>
                    <p className="text-2xl font-bold">{analytics?.data?.activeTenants || 0}</p>
                  </div>
                  <CheckCircleIcon className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue (MTD)</p>
                    <p className="text-2xl font-bold">KES {(analytics?.data?.totalRevenue || 0).toLocaleString()}</p>
                  </div>
                  <CurrencyDollarIcon className="h-8 w-8 text-yellow-500" />
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Users</p>
                    <p className="text-2xl font-bold">{analytics?.data?.totalUsers || 0}</p>
                  </div>
                  <UsersIcon className="h-8 w-8 text-purple-500" />
                </div>
              </div>
            </div>

            {/* Top Tenants */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Top Performing Tenants</h3>
              <div className="space-y-3">
                {analytics?.data?.topTenants?.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No tenant data available</p>
                ) : (
                  analytics?.data?.topTenants?.map((tenant, index) => (
                    <div key={tenant.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="font-bold text-gray-500">#{index + 1}</span>
                        <div>
                          <p className="font-medium">{tenant.business_name}</p>
                          <p className="text-xs text-gray-500">Plan: {tenant.subscription_plan}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">KES {tenant.revenue?.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{tenant.transaction_count} transactions</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">System Revenue Report</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-600">Total Revenue (MTD)</p>
                  <p className="text-2xl font-bold text-green-700">
                    KES {(analytics?.data?.totalRevenue || 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-600">Active Subscriptions</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {analytics?.data?.activeTenants || 0}
                  </p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-purple-700">
                    {(analytics?.data?.totalTransactions || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create Tenant Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New Tenant"
        size="large"
      >
        <TenantForm onSuccess={() => {
          setShowCreateModal(false);
          queryClient.invalidateQueries(['superadmin', 'tenants']);
        }} />
      </Modal>

      {/* Tenant Details Modal with Enhanced Features */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title="Tenant Details"
        size="large"
      >
        {selectedTenant && (
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <h4 className="font-semibold text-gray-900 border-b pb-2 mb-2">Basic Information</h4>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Business Name</label>
                <p className="mt-1 text-sm text-gray-900 font-medium">{selectedTenant.business_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="mt-1">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    selectedTenant.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedTenant.is_active ? 'Active' : 'Suspended'}
                  </span>
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTenant.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTenant.phone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">TIN/PIN</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTenant.tin_number || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <p className="mt-1 text-sm text-gray-900">{selectedTenant.address || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Registered On</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedTenant.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Subscription Information */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-900 border-b pb-2 mb-2">Subscription Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Plan</label>
                  <p className="mt-1 text-sm text-gray-900 capitalize">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedTenant.subscription_plan === 'premium' ? 'bg-purple-100 text-purple-800' :
                      selectedTenant.subscription_plan === 'enterprise' ? 'bg-amber-100 text-amber-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedTenant.subscription_plan?.toUpperCase()}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedTenant.subscription_expiry ? new Date(selectedTenant.subscription_expiry).toLocaleDateString() : 'N/A'}
                    {selectedTenant.subscription_expiry && new Date(selectedTenant.subscription_expiry) < new Date() && (
                      <span className="ml-2 text-red-600 text-xs">(Expired)</span>
                    )}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">SMS Balance</label>
                  <p className="mt-1 text-sm text-gray-900 font-semibold text-blue-600">
                    KES {selectedTenant.sms_balance?.toLocaleString() || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-gray-900 border-b pb-2 mb-2">Statistics</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">-</p>
                  <p className="text-xs text-gray-500">Total Users</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">-</p>
                  <p className="text-xs text-gray-500">Total Products</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">-</p>
                  <p className="text-xs text-gray-500">Total Transactions</p>
                </div>
              </div>
            </div>
            
            {/* Quick Actions */}
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium mb-3">Quick Actions</h4>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleUpdatePlan(selectedTenant)}
                  className="flex items-center justify-center px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 transition-colors"
                >
                  <PencilIcon className="h-4 w-4 mr-2" />
                  Update Plan
                </button>
                <button
                  onClick={() => handleExtendByDays(selectedTenant)}
                  className="flex items-center justify-center px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm hover:bg-green-200 transition-colors"
                >
                  <CheckBadgeIcon className="h-4 w-4 mr-2" />
                  Extend by Days
                </button>
                <button
                  onClick={() => handleAddSmsCredit(selectedTenant)}
                  className="flex items-center justify-center px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200 transition-colors"
                >
                  <CreditCardIcon className="h-4 w-4 mr-2" />
                  Add SMS Credit
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleSuspendTenant(selectedTenant);
                  }}
                  className={`flex items-center justify-center px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedTenant.is_active 
                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  <NoSymbolIcon className="h-4 w-4 mr-2" />
                  {selectedTenant.is_active ? 'Suspend Tenant' : 'Activate Tenant'}
                </button>
                <button
                  onClick={() => {
                    // Send test email
                    toast.info(`Test email sent to ${selectedTenant.email}`);
                  }}
                  className="flex items-center justify-center px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm hover:bg-yellow-200 transition-colors col-span-2"
                >
                  <EnvelopeIcon className="h-4 w-4 mr-2" />
                  Send Email Notification
                </button>
              </div>
            </div>

            {/* Danger Zone */}
            <div className="border-t border-red-200 pt-4 mt-2">
              <h4 className="font-medium text-red-600 mb-2">Danger Zone</h4>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleDeleteTenant(selectedTenant);
                }}
                className="w-full flex items-center justify-center px-3 py-2 bg-red-50 text-red-700 rounded-lg text-sm hover:bg-red-100 transition-colors"
              >
                <TrashIcon className="h-4 w-4 mr-2" />
                Deactivate Tenant
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Suspend/Activate Confirmation Modal */}
      <Modal
        isOpen={showSuspendConfirm}
        onClose={() => setShowSuspendConfirm(false)}
        title={`${selectedTenant?.is_active ? 'Suspend' : 'Activate'} Tenant`}
        size="sm"
      >
        <div className="text-center">
          <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${selectedTenant?.is_active ? 'bg-red-100' : 'bg-green-100'}`}>
            {selectedTenant?.is_active ? (
              <NoSymbolIcon className="h-6 w-6 text-red-600" />
            ) : (
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            )}
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {selectedTenant?.is_active ? 'Suspend Tenant' : 'Activate Tenant'}
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            {selectedTenant?.is_active 
              ? `Are you sure you want to suspend ${selectedTenant?.business_name}? This will prevent all users from accessing the system.`
              : `Are you sure you want to activate ${selectedTenant?.business_name}? This will restore access for all users.`
            }
          </p>
          <div className="mt-4 flex space-x-3">
            <button
              onClick={() => setShowSuspendConfirm(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => suspendTenantMutation.mutate({ 
                tenantId: selectedTenant?.id, 
                suspend: selectedTenant?.is_active 
              })}
              disabled={suspendTenantMutation.isPending}
              className={`flex-1 px-4 py-2 text-white rounded-lg ${
                selectedTenant?.is_active 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'
              } disabled:opacity-50`}
            >
              {suspendTenantMutation.isPending ? 'Processing...' : (selectedTenant?.is_active ? 'Suspend' : 'Activate')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Deactivate Tenant"
        size="sm"
      >
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
            <TrashIcon className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Deactivate Tenant</h3>
          <p className="mt-2 text-sm text-gray-500">
            Are you sure you want to deactivate <strong>{selectedTenant?.business_name}</strong>?
            This will deactivate all users and prevent access. Data will be preserved.
          </p>
          <div className="mt-4 flex space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={() => deleteTenantMutation.mutate(selectedTenant?.id)}
              disabled={deleteTenantMutation.isPending}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {deleteTenantMutation.isPending ? 'Processing...' : 'Deactivate'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}